import { Box, Divider, Drawer, List, ListItemIcon, ListItemText, ListItemButton, Collapse, useTheme } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import { routes } from '../../config/routes';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import GlobalErrorSnackbar from '../GlobalErrorSnackbar';
import { useDisplaySize } from '../../contexts/DisplaySizeContext';
import { useAuth } from '../../contexts/AuthContext';

const Layout: React.FC = () => {
    const theme = useTheme();
    const { size } = useDisplaySize();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});

    // Calculate responsive drawer width based on display size
    const getDrawerWidth = () => {
        switch (size) {
            case 'extra small':
                return 200;
            case 'small':
                return 240;
            case 'large':
                return 320;
            default:
                return 280;
        }
    };

    const drawerWidth = getDrawerWidth();

    // Update open menus based on current path
    useEffect(() => {
        const path = location.pathname;
        const newOpenMenus: { [key: string]: boolean } = {};
        
        routes.forEach(route => {
            if (route.children) {
                const hasActiveChild = route.children.some(child => path.startsWith(child.path));
                if (hasActiveChild) {
                    newOpenMenus[route.id] = true;
                }
            }
        });
        
        setOpenMenus(prev => ({
            ...prev,
            ...newOpenMenus
        }));
    }, [location.pathname]);

    const handleMenuClick = (menuId: string) => {
        setOpenMenus(prev => ({
            ...prev,
            [menuId]: !prev[menuId],
        }));
    };

    const handleNavigate = (path: string) => {
        navigate(path);
    };

    const isSelected = (path: string) => location.pathname === path;

    const hasSelectedChild = (item: typeof routes[0]) => {
        return item.children?.some(child => location.pathname.startsWith(child.path));
    };

    const renderMenuItem = (item: typeof routes[0]) => {
        if (item.children) {
            const nestedPl =
                size === 'extra small' ? 4 : size === 'small' ? 6 : size === 'large' ? 8 : 7;
            const isChildSelected = hasSelectedChild(item);
            return (
                <>
                    <ListItemButton
                        onClick={() => handleMenuClick(item.id)}
                        sx={{
                            mb: 0.5,
                            mx: 1,
                            borderRadius: 0,
                            backgroundColor: 'transparent',
                            borderLeft: isChildSelected ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                            '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                            },
                            color: theme.palette.text.primary,
                        }}
                    >
                        <ListItemIcon sx={{ 
                            minWidth: size === 'extra small' ? 24 : size === 'small' ? 32 : size === 'large' ? 48 : 40,
                            color: 'inherit'
                        }}>
                            {item.icon
                                ? React.cloneElement(item.icon as React.ReactElement<any>, {
                                    style: { fontSize: size === 'extra small' ? '0.875rem' : size === 'small' ? '1rem' : size === 'large' ? '1.5rem' : '1.25rem' }
                                } as any)
                                : null}
                        </ListItemIcon>
                        <ListItemText 
                            primary={item.label}
                            sx={{
                                '& .MuiTypography-root': {
                                    fontSize: size === 'extra small' ? '0.625rem' : size === 'small' ? '0.75rem' : size === 'large' ? '1rem' : '0.875rem',
                                    fontWeight: isChildSelected ? 600 : 500,
                                    color: 'inherit'
                                }
                            }}
                        />
                        {openMenus[item.id] ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={openMenus[item.id]} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {item.children.filter(child => !child.hideFromMenu && (!child.requiredPermission || user?.permissions?.includes(child.requiredPermission))).map((child, index) => (
                                <React.Fragment key={child.id}>
                                    <ListItemButton
                                        onClick={() => handleNavigate(child.path)}
                                        selected={isSelected(child.path)}
                                        sx={{
                                            pl: nestedPl,
                                            py: size === 'extra small' ? 0.25 : size === 'small' ? 0.5 : size === 'large' ? 1.5 : 1,
                                            mx: 1,
                                            borderRadius: theme.shape.borderRadius,
                                            '&.Mui-selected': {
                                                backgroundColor: theme.palette.primary.main,
                                                color: theme.palette.primary.contrastText,
                                                '&:hover': {
                                                    backgroundColor: theme.palette.primary.dark,
                                                },
                                                '& .MuiListItemIcon-root': {
                                                    color: theme.palette.primary.contrastText,
                                                },
                                            },
                                            '&:hover': {
                                                backgroundColor: theme.palette.action.hover,
                                            },
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: size === 'extra small' ? 24 : size === 'small' ? 32 : size === 'large' ? 48 : 40 }}>
                                            {child.icon
                                                ? React.cloneElement(child.icon as React.ReactElement<any>, {
                                                    style: { fontSize: size === 'extra small' ? '0.875rem' : size === 'small' ? '1rem' : size === 'large' ? '1.5rem' : '1.25rem' }
                                                } as any)
                                                : null}
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary={child.label}
                                            sx={{
                                                '& .MuiTypography-root': {
                                                    fontSize: size === 'extra small' ? '0.625rem' : size === 'small' ? '0.75rem' : size === 'large' ? '1rem' : '0.875rem',
                                                    fontWeight: 500
                                                }
                                            }}
                                        />
                                    </ListItemButton>
                                    {item.id === 'setup' && index === 0 ? (
                                        <Divider
                                            component="div"
                                            sx={{
                                                ml: theme.spacing(1 + nestedPl),
                                                mr: theme.spacing(1),
                                                my: 0.5,
                                                borderColor: theme.palette.divider,
                                                opacity: 0.55
                                            }}
                                        />
                                    ) : null}
                                </React.Fragment>
                            ))}
                        </List>
                    </Collapse>
                </>
            );
        }

        return (
            <ListItemButton
                onClick={() => handleNavigate(item.path)}
                selected={isSelected(item.path)}
                sx={{
                    mb: 0.5,
                    mx: 1,
                    borderRadius: theme.shape.borderRadius,
                    '&.Mui-selected': {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                        },
                        '& .MuiListItemIcon-root': {
                            color: theme.palette.primary.contrastText,
                        },
                    },
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                    },
                }}
            >
                <ListItemIcon sx={{ minWidth: size === 'extra small' ? 24 : size === 'small' ? 32 : size === 'large' ? 48 : 40 }}>
                    {item.icon
                        ? React.cloneElement(item.icon as React.ReactElement<any>, {
                            style: { fontSize: size === 'extra small' ? '0.875rem' : size === 'small' ? '1rem' : size === 'large' ? '1.5rem' : '1.25rem' }
                        } as any)
                        : null}
                </ListItemIcon>
                <ListItemText 
                    primary={item.label}
                    sx={{
                        '& .MuiTypography-root': {
                            fontSize: size === 'extra small' ? '0.625rem' : size === 'small' ? '0.75rem' : size === 'large' ? '1rem' : '0.875rem',
                            fontWeight: 500
                        }
                    }}
                />
            </ListItemButton>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Navbar />
            <Box sx={{ display: 'flex', flex: 1 }}>
                <Drawer
                    variant="permanent"
                    sx={{
                        width: drawerWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            position: 'relative',
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            bgcolor: theme.palette.background.paper,
                            borderRight: `1px solid ${theme.palette.divider}`,
                            height: '100%',
                        },
                    }}
                >
                    <List component="nav" sx={{ pt: size === 'extra small' ? 0.5 : size === 'small' ? 1 : size === 'large' ? 3 : 2 }}>
                        {routes.filter(item => !item.hideFromMenu && (!item.requiredPermission || user?.permissions?.includes(item.requiredPermission))).map((item) => (
                            <React.Fragment key={item.id}>
                                {renderMenuItem(item)}
                            </React.Fragment>
                        ))}
                    </List>
                </Drawer>

                <Box 
                    component="main" 
                    sx={{ 
                        flexGrow: 1, 
                        display: 'flex', 
                        flexDirection: 'column',
                        bgcolor: theme.palette.background.default,
                        p: size === 'extra small' ? 1 : size === 'small' ? 2 : size === 'large' ? 4 : 3
                    }}
                >
                    <Outlet />
                </Box>
            </Box>
            <GlobalErrorSnackbar />
        </Box>
    );
};

export default Layout;
